import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { object, string } from "yup";
import { toast } from "react-toastify";
import ReCAPTCHA from "react-google-recaptcha";
import { login } from "../../services/AuthServices";
import authIllustrations from "../../assets/images/auth-illustrations.png";
import "./Auth.css";

const MainSection = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const recaptchaRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const onRecaptchaChange = (token) => setRecaptchaToken(token);
  const onRecaptchaExpired = () => setRecaptchaToken(null);
  const onRecaptchaError = () => setRecaptchaError(true);

  const resetRecaptcha = () => {
    setRecaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const validationSchema = object().shape({
    email: string()
      .required("Please enter your email")
      .email("Please enter a valid email address")
      .trim(),
    password: string()
      .required("Please enter your password")
      .trim(),
  });

  const submitLogin = async (data) => {
    setIsLoading(true);
    let formdata = new FormData();
    formdata.append("email", data.email);
    formdata.append("password", data.password);

    try {
      const response = await login(formdata);
      localStorage.setItem("is_authenticated", 1);
      localStorage.setItem("token", response?.data?.token);
      localStorage.setItem("userId", response?.data?.data?.id);
      localStorage.setItem("full_name", response?.data?.data?.full_name);
      toast("Login successful! Welcome back.", { type: "success" });
      setIsLoading(false);
      navigate("/");
    } catch (error) {
      setIsLoading(false);
      resetRecaptcha();
      const message = error?.response?.data?.message || "Something went wrong";
      toast(message, { type: "error" });
    }
  };

  const { handleSubmit, handleBlur, handleChange, values, errors, touched } =
    useFormik({
      validationSchema,
      initialValues: { email: "", password: "" },
      onSubmit: async (values) => {
        if (!recaptchaError && !recaptchaToken) {
          toast("Please complete the reCAPTCHA verification.", { type: "error" });
          return;
        }
        submitLogin(values);
      },
    });

  return (
    <div className="dp-auth-container">
      <div className="dp-auth-card">
        {/* Left Side - Illustration */}
        <div className="dp-auth-left">
          <div
            className="dp-auth-illustration-wrapper login"
            style={{ backgroundImage: `url(${authIllustrations})` }}
          ></div>
          <div className="dp-auth-brand">
            <h2>Welcome Back!</h2>
            <p>Login to continue your winning journey and access exclusive draws.</p>
            <div className="dp-auth-features">
              <div className="dp-auth-feature">
                <i className="fas fa-trophy"></i>
                <span>Win amazing prizes</span>
              </div>
              <div className="dp-auth-feature">
                <i className="fas fa-shield-alt"></i>
                <span>Secure & trusted platform</span>
              </div>
              <div className="dp-auth-feature">
                <i className="fas fa-bolt"></i>
                <span>Instant withdrawals</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="dp-auth-right">
          <div className="dp-auth-header">
            <h1>Login</h1>
            <p>Enter your credentials to access your account</p>
          </div>

          <form className="dp-auth-form" onSubmit={handleSubmit}>
            {/* Email Field */}
            <div className="dp-form-group">
              <label className="dp-form-label">Email Address</label>
              <div className="dp-form-input-wrapper">
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  className={`dp-form-input ${touched.email && errors.email ? 'error' : ''}`}
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <i className="fal fa-envelope dp-form-icon"></i>
              </div>
              {touched.email && errors.email && (
                <span className="dp-form-error">{errors.email}</span>
              )}
            </div>

            {/* Password Field */}
            <div className="dp-form-group">
              <label className="dp-form-label">Password</label>
              <div className="dp-form-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  className={`dp-form-input ${touched.password && errors.password ? 'error' : ''}`}
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <i
                  className={`fal ${showPassword ? 'fa-eye' : 'fa-eye-slash'} dp-form-icon clickable`}
                  onClick={() => setShowPassword(!showPassword)}
                ></i>
              </div>
              {touched.password && errors.password && (
                <span className="dp-form-error">{errors.password}</span>
              )}
            </div>

            {/* Forgot Password */}
            <div className="dp-form-forgot">
              <span onClick={() => navigate("/forgot-password")}>
                Forgot Password?
              </span>
            </div>

            {/* reCAPTCHA */}
            {!recaptchaError && (
              <div className="dp-recaptcha-wrapper">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={onRecaptchaChange}
                  onExpired={onRecaptchaExpired}
                  onErrored={onRecaptchaError}
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="dp-auth-submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Logging in...
                </>
              ) : (
                <>
                  <i className="far fa-sign-in"></i>
                  Login
                </>
              )}
            </button>

            {/* Footer */}
            <div className="dp-auth-footer">
              <p>
                Don't have an account?{" "}
                <span onClick={() => navigate("/register")}>Create Account</span>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MainSection;
